import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { filter, find, includes, map, startCase, toLower } from 'lodash';
import { Subject } from 'rxjs';
import { IAssignSupportModalComponentData } from 'src/ng2/shared/modals/assign-support/assign-support-modal.component';
import { ISchoolSupport } from 'Src/ng2/shared/typings/interfaces/support.interface';
import { ImUser } from '../../../shared/services/im-models/im-user';
import { ISchool } from '../../../shared/typings/interfaces/school.interface';
import { IStudent } from '../../../shared/typings/interfaces/student.interface';
import { IUser } from '../../../shared/typings/interfaces/user.interface';
import { IUpdatePayload } from '../../../store';
import { IDocLog } from './../../../../app/shared/models/doc-log/doc-log';
import { AssignSupportModalService } from './../../../shared/modals/assign-support/assign-support-modal.service';
import { IFlagIgnoreModalResult } from './../../../shared/modals/flag-ignore/flag-ignore-modal.component';
import { IFlagLogActionModalResult } from './../../../shared/modals/flag-log-action/flag-log-action-modal.component';
import {
  HistoryAllHelperService,
  IHistoryRecord,
} from './../../../shared/modals/history-all/history-all-helpers.service';
import { ModalsService } from './../../../shared/modals/modals.service';
import { IFlag } from './../../../shared/typings/interfaces/flags.interface';

declare const window;

@Component({
  selector: 'ssv-side-nav-primary',
  template: require('./ssv-side-nav-primary.component.html'),
  styles: [require('./ssv-side-nav-primary.component.scss')],
})
export class SsvSideNavPrimary implements OnInit, OnChanges {
  @Input()
  currentUser: IUser;
  @Input()
  currentStudent: IStudent;
  @Input()
  currentSchool: ISchool;
  @Input()
  flags: IFlag[];
  @Input()
  docLogs: IDocLog[];
  @Input()
  ssvExitState: string;
  @Output()
  exitStudentClick: EventEmitter<any> = new EventEmitter();
  @Output()
  updateFlag: EventEmitter<IUpdatePayload<IFlag>> = new EventEmitter();
  @Output()
  studentSupportCreatedEvent: EventEmitter<any> = new EventEmitter();

  note;
  student;
  flagsWithOptions;
  hasActiveFlags: boolean;
  recentHistory: IHistoryRecord[];
  hasHistory: boolean;
  hasMoreHistory: boolean;
  isEditingUser: boolean;
  destroy$: Subject<boolean> = new Subject<boolean>();

  readonly FlagMenuOptions = {
    ASSIGN_SUPPORT: 'Assign support',
    IGNORE: 'Ignore',
    LOG_ACTION: 'Log action',
  };

  readonly FlagMenuOptionsToModalMap = {
    [this.FlagMenuOptions.ASSIGN_SUPPORT]: this.openFlagsAssignSupportModal.bind(this),
    [this.FlagMenuOptions.IGNORE]: this.openFlagsIgnoreModal.bind(this),
    [this.FlagMenuOptions.LOG_ACTION]: this.openFlagsLogActionModal.bind(this),
  };

  constructor(
    private modalsService: ModalsService,
    private historyAllHelpers: HistoryAllHelperService,
    private assignSupportModalService: AssignSupportModalService,
    private imUser: ImUser,
  ) {
    //
  }

  public ngOnInit(): void {
    this.isEditingUser = this.imUser.isEditingUser(this.currentUser);
    this.setFlags();
    this.setDocLogs();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { flags, docLogs } = changes;

    if (flags && !flags.firstChange) this.setFlags();
    if (docLogs && !docLogs.firstChange) this.setDocLogs();
  }

  setFlags() {
    const activeFlags = filter(this.flags, (flag: IFlag) => {
      return flag.status === 'ACTIVE' && !flag.isIgnored;
    });

    this.flagsWithOptions = map(activeFlags, (flag: IFlag) => {
      const type = flag.type && flag.type.toLowerCase();
      let options;

      // keep everything lowercase to avoid casing mismatches with data (CM)
      let cat1 = ['Needs attendance support', 'Needs ELA support', 'Needs Math support'].map(s => s.toLowerCase());
      let cat2 = 'Absent today'.toLowerCase();

      // Other possible flag type for future testing (JE)
      // let cat3 = 'Failing high priority course(s)';

      if (includes(cat1, type)) options = [this.FlagMenuOptions.ASSIGN_SUPPORT, this.FlagMenuOptions.IGNORE];
      else if (cat2 === type) options = [this.FlagMenuOptions.LOG_ACTION, this.FlagMenuOptions.IGNORE];
      else
        options = [this.FlagMenuOptions.ASSIGN_SUPPORT, this.FlagMenuOptions.LOG_ACTION, this.FlagMenuOptions.IGNORE];

      const flagWithOptions = {
        _id: flag._id,
        category: flag.category,
        studentId: flag.studentId,
        type: flag.type,
        options,
      };

      return flagWithOptions;
    });

    this.hasActiveFlags = activeFlags.length > 0;
  }

  setDocLogs(): void {
    // doc logs on the store can be for multiple students, filter them out (JE)
    const filteredLogs = filter(this.docLogs, (docLog: IDocLog) => {
      return docLog.studentId === this.currentStudent._id;
    });

    const sortedLogs = filteredLogs.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      if (dateA > dateB) return -1;
      else if (dateB > dateA) return 1;
      else return 0;
    });

    const historyLogs = map(sortedLogs, (docLog: IDocLog) => {
      return this.historyAllHelpers.constructHistoryRecord(docLog);
    });
    const filteredData = filter(historyLogs, (historyItem: IHistoryRecord) => {
      return !!historyItem;
    });

    this.recentHistory = filteredData.slice(0, 3);
    this.hasHistory = this.recentHistory.length > 0;
    this.hasMoreHistory = historyLogs.length > 3;
  }

  // helper
  convertCase(name): string {
    return startCase(toLower(name));
  }

  // events
  exitStudent(): void {
    this.exitStudentClick.emit();
  }

  toggleHistoryItem(id: string) {
    const clickedItem = find(this.recentHistory, item => {
      return item.id === id;
    });
    if (clickedItem.panelOpen) {
      clickedItem.panelOpen = false;
    } else {
      this.recentHistory = map(this.recentHistory, item => {
        item.panelOpen = false;
        return item;
      });
      clickedItem.panelOpen = true;
    }
  }

  openFlagsLogActionModal(flagId: string) {
    const flag = find(this.flags, flag => flag._id === flagId);
    this.modalsService
      .openFlagsLogActionModal(flag)
      .afterClosed()
      .subscribe((result: IFlagLogActionModalResult) => {
        // If !result, then the user canceled action in modal (CM).
        const payload = {
          id: flagId,
          patch: result,
        };
        this.updateFlag.emit(payload);
      });
  }

  openFlagsIgnoreModal(flagId: string) {
    const flag = find(this.flags, flag => flag._id === flagId);
    this.modalsService
      .openFlagsIgnoreModal(flag)
      .afterClosed()
      .subscribe((result: IFlagIgnoreModalResult) => {
        const payload = {
          id: flagId,
          patch: result,
        };
        this.updateFlag.emit(payload);
      });
  }

  // TODO: add assigned support modal logic here when assigned support modal v3 feature is added (CM).
  /* istanbul ignore next */
  openFlagsAssignSupportModal(flagId: string) {
    const { category } = find(this.flags, flag => flag._id === flagId);
    const { _id: studentId } = this.currentStudent;
    const { _id: schoolId } = this.currentSchool;

    const data: IAssignSupportModalComponentData = {
      schoolId,
      studentIds: [studentId],
      category,
      isProfileMode: true,
    };
    this.modalsService
      .openAssignSupportModal(data)
      .afterClosed()
      .subscribe((selectedSupport: ISchoolSupport) => {
        // If !selectedSupport, then the user canceled action in modal (CM).
        if (selectedSupport) {
          const payload = {
            support: selectedSupport,
            studentId,
            schoolId,
          };
          this.assignSupportModalService.createStudentSupport(payload);
        }
      });
  }

  openShowAllHistoryModal() {
    const data = { student: this.currentStudent };
    this.modalsService.openHistoryModal(data);
  }
}
